import type { Member } from '../../types';
import { todayISO } from '../../utils/format';

// A guest is a member record with Member Status "Guest": someone attending for
// a period — a posting, a course, a family visit — who is not on the roll.
// The stay has a from/until date; when it ends they either leave (Transferred
// back to their home church) or are taken onto the roll (Regular, with the
// date stamped in membershipDate). Everything here is shared by the member
// form, the Guests tab, the dashboard and the import sheet.

/** The Member Status value that makes a record a guest. Compared case-insensitively. */
export const GUEST_STATUS = 'Guest';

/** The Member Status a guest gets when taken onto the roll. */
export const ROLL_STATUS = 'Regular';

/** A stay ending within this many days is "ending soon". Matches the backend. */
export const EXPIRING_DAYS = 30;

/** Fallback when Settings has no guest period. */
export const DEFAULT_GUEST_MONTHS = 12;

export const isGuest = (m?: Pick<Member, 'memberStatus'> | null): boolean =>
  String(m?.memberStatus || '').trim().toLowerCase() === GUEST_STATUS.toLowerCase();

/** ISO date + n months, clamped to the month's last day (31 Jan + 1 → 28/29 Feb). */
export const addMonths = (iso: string, months: number): string => {
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return '';
  const target = new Date(y, m - 1 + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(d, lastDay));
  const mm = String(target.getMonth() + 1).padStart(2, '0');
  const dd = String(target.getDate()).padStart(2, '0');
  return `${target.getFullYear()}-${mm}-${dd}`;
};

/** Whole days from `today` to `until`; negative once it has passed. */
export const daysUntil = (until?: string | null, today: string = todayISO()): number | null => {
  if (!until) return null;
  const a = new Date(`${today}T00:00:00`);
  const b = new Date(`${until}T00:00:00`);
  if (isNaN(a.getTime()) || isNaN(b.getTime())) return null;
  return Math.round((b.getTime() - a.getTime()) / 86400000);
};

export type GuestPhase = 'open' | 'active' | 'expiring' | 'expired';

/**
 * Where the stay stands: no end date (open), running, ending within
 * EXPIRING_DAYS, or already past. Same thresholds as the backend's buckets.
 */
export const guestPhase = (m: Pick<Member, 'guestUntil'>, today: string = todayISO()): GuestPhase => {
  const days = daysUntil(m.guestUntil, today);
  if (days === null) return 'open';
  if (days < 0) return 'expired';
  if (days <= EXPIRING_DAYS) return 'expiring';
  return 'active';
};

/** Badge wording for a phase. */
export const guestPhaseLabel = (phase: GuestPhase, days: number | null): string => {
  switch (phase) {
    case 'expired': return `Ended ${Math.abs(days ?? 0)} day${Math.abs(days ?? 0) === 1 ? '' : 's'} ago`;
    case 'expiring': return days === 0 ? 'Ends today' : `${days} day${days === 1 ? '' : 's'} left`;
    case 'active': return `${days} days left`;
    default: return 'No end date';
  }
};
